import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { CopyIcon, Eye, MoreVertical, Wifi } from 'lucide-react';
import { parseRouterConfigSettings, RouterConfig } from '@/shared/types/routerTypes';
import { WIFI_RF_BAND_VALUES } from '@/shared/types/routerTypes';
import Dropdown from '@/shared/components/Dropdown';

interface RouterConfigCardProps {
  config: RouterConfig;
  onView?: () => void;
  onDuplicate?: () => void;
}

const RouterConfigCard = ({ config, onView, onDuplicate }: RouterConfigCardProps) => {
  const dropdownItems = useMemo(() => {
    const items: {
      label: string;
      icon: typeof CopyIcon;
      variant: 'default' | 'destructive';
      onClick: () => void;
    }[] = [];
    if (onView) {
      items.push({ label: 'View configuration', icon: Eye, variant: 'default', onClick: onView });
    }
    if (onDuplicate) {
      items.push({ label: 'Duplicate', icon: CopyIcon, variant: 'default', onClick: onDuplicate });
    }
    return items;
  }, [onView, onDuplicate]);

  const settings = parseRouterConfigSettings(config.routerConfigJson);
  const network = settings?.networks?.[0];
  const bssList = network?.basicServiceSets ?? [];
  const ssidSummary = bssList
    .map((bss) => `${WIFI_RF_BAND_VALUES[bss.band] ?? bss.band}: ${bss.ssid || '-'}`)
    .join(' · ');

  return (
    <Card className="border border-border">
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border shrink-0">
              <Wifi className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg truncate">{config.nickname || 'Unnamed config'}</CardTitle>
              <CardDescription className="text-sm truncate">
                {settings?.bypassMode
                  ? 'Bypass mode — Starlink Wi-Fi off'
                  : ssidSummary || 'No networks configured'}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {settings?.bypassMode ? (
              <Badge className="bg-amber-500 text-white">Bypass</Badge>
            ) : (
              <Badge variant="outline">Wi-Fi</Badge>
            )}
            {dropdownItems.length > 0 && (
              <Dropdown triggerIcon={MoreVertical} items={dropdownItems} />
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          {!settings?.bypassMode && bssList.map((bss) => (
            <Badge key={bss.band} variant="outline">
              {WIFI_RF_BAND_VALUES[bss.band] ?? bss.band}: {bss.ssid || '-'}
              {bss.hidden ? ' (hidden)' : ''}
            </Badge>
          ))}
          {network?.clientIsolation && <Badge variant="outline">Client isolation</Badge>}
          {settings?.applyNetworks && <Badge variant="outline">Apply networks</Badge>}
          {settings?.outdoorMode && <Badge variant="outline">Outdoor</Badge>}
          {settings?.dfsEnabled && <Badge variant="outline">DFS</Badge>}
          {settings?.disable2ghz && <Badge variant="outline">2.4 GHz off</Badge>}
          {settings?.disable5ghz && <Badge variant="outline">5 GHz off</Badge>}
          {settings?.disable5ghzHigh && <Badge variant="outline">5 GHz high off</Badge>}
        </div>
      </CardContent>
    </Card>
  );
};

export default RouterConfigCard;
